import { useState, useEffect, useRef } from 'react'
import * as React from 'react'
import HeroImage from 'content/components/HeroImage'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import styled from 'styled-components'
import TripLandingPageHeader from './TripLandingPageHeader'
import Group from 'components/utils/Group'
import TripLandingPageHowItWorks from './TripLandingPageHowItWorks'
import TripLandingPageWhyYoullLoveIt from './TripLandingPageWhyYoullLoveIt'
import TripLandingPageAndTheresMore from './TripLandingPageAndTheresMore'
import TripLandingPageTrendingDestinations from './TripLandingPageTrendingDestinations'
import TripLandingPageFooter from './TripLandingPageFooter'
import { useInView } from 'react-intersection-observer'
import zIndex from 'styles/tools/z-index'
import TabButton from 'LuxKit/Tabs/TabButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import type { TabName } from './LoggedInTripPlannerLandingPage'
import { useAppHeaderObserver } from 'hooks/useAppLayoutObservers'
import { FormattedMessage } from 'react-intl'
import TripLandingPageLookingForAi from './TripLandingPageLookingForAi'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

const HeroImageWrapper = styled(HeroImage)`
 min-height: ${rem(400)};

 ${mediaQueryUp.tablet} {
   min-height: ${rem(380)};
 }
`

const ScrollableButtonGroup = styled.div`
  overflow-x: auto;
  background: ${({ theme }) => theme.palette.neutral.surface.normalBackground};
  
  &::-webkit-scrollbar {
    display: none;
  }
  
`

const StickyButtonGroup = styled(ScrollableButtonGroup)`
  position: fixed;
  top: 0;
  inset-inline-start: 50%;
  transform: translateX(-50%);
  z-index: ${zIndex.floatingPanel};
  width: 100%;

  [dir='rtl'] & {
    transform: translateX(50%);
  }
`

function LoggedOutTripPlannerLandingPage() {
  const [isStickyHeaderVisible] = useState(true)
  const [activeTab, setActiveTab] = useState<TabName>('howitworks')
  const stickyButtonGroupRef = useRef<HTMLDivElement>(null)

  useAppHeaderObserver(stickyButtonGroupRef, 'sticky')

  const { ref: heroRef, inView: isHeroInView } = useInView({
    threshold: 0.3,
  })

  const { ref: howItWorksRef, inView: isHowItWorksInView } = useInView({
    threshold: 0.3,
  })

  const { ref: whyYoullLoveItRef, inView: isWhyYoullLoveItInView } = useInView({
    threshold: 0.1,
  })

  const { ref: whatOurTravellersSayRef, inView: isWhatOurTravellersSayInView } = useInView({
    threshold: 0.9,
  })

  const { ref: destinationsRef, inView: isDestinationsInView } = useInView({
    threshold: 0.3,
  })

  useEffect(() => {
    if (isHowItWorksInView) {
      setActiveTab('howitworks')
    } else if (isWhyYoullLoveItInView) {
      setActiveTab('whyyoullloveit')
    } else if (isWhatOurTravellersSayInView) {
      setActiveTab('whatourtravellerssay')
    } else if (isDestinationsInView) {
      setActiveTab('destinations')
    }
  }, [isHowItWorksInView, isWhyYoullLoveItInView, isWhatOurTravellersSayInView, isDestinationsInView])

  const handleButtonClick = (anchorId: TabName) => (e: React.MouseEvent) => {
    e.preventDefault()
    setActiveTab(anchorId)

    const element = document.getElementById(anchorId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const ButtonGroup = (
    <Group direction="horizontal" horizontalAlign="start" verticalAlign="center" desktopHorizontalAlign="center" tabletHorizontalAlign="center">
      <TabButton
        onClick={handleButtonClick('howitworks')}
        size="medium"
        isActive={activeTab === 'howitworks'}
      >
        <FormattedMessage
          id="DuxP2e4W4TeJHYzRpEDy"
          defaultMessage="How it works"
          description="Tab button label for How it works section in the trip planner"
        />
      </TabButton>
      <TabButton
        onClick={handleButtonClick('whyyoullloveit')}
        size="medium"
        isActive={activeTab === 'whyyoullloveit'}
      >
        <FormattedMessage
          id="y3SxseQK7769kKYnjjvB"
          defaultMessage="Why you’ll love it"
          description="Tab button label for Why you'll love it section in the trip planner"
        />
      </TabButton>
      <TabButton
        onClick={handleButtonClick('whatourtravellerssay')}
        size="medium"
        isActive={activeTab === 'whatourtravellerssay'}
      >
        <FormattedMessage
          id="lcNTsqWR9Ce1URUmg8i3"
          defaultMessage="What our travellers say"
          description="Tab button label for What our travellers say section"
        />
      </TabButton>
      <TabButton
        onClick={handleButtonClick('destinations')}
        size="medium"
        isActive={activeTab === 'destinations'}
      >
        <FormattedMessage
          id="-MVIWGWdTBtVURUcyane"
          defaultMessage="Destinations"
          description="Tab button label for the Destinations section in the trip planner"
        />
      </TabButton>
    </Group>
  )

  return (
    <div>
      {isStickyHeaderVisible && !isHeroInView && (
        <StickyButtonGroup ref={stickyButtonGroupRef}>
          {ButtonGroup}
        </StickyButtonGroup>
      )}
      <HeroImageWrapper
        ref={heroRef}
        id="nimf0j6h3ldjk6c9qkj"
        gravity="south"
        extraLargeDesktopAspectRatio="144:38"
        quality="best"
      >
        <LayoutContainer>
          <TripLandingPageHeader loggedIn={false} />
        </LayoutContainer>
      </HeroImageWrapper>
      {isHeroInView && (
        <LayoutContainer>
          <ScrollableButtonGroup>
            {ButtonGroup}
          </ScrollableButtonGroup>
        </LayoutContainer>
      )}
      <CSSBreakpoint max="tablet">
        <TripLandingPageLookingForAi />
      </CSSBreakpoint>
      <div id="howitworks" ref={howItWorksRef}>
        <TripLandingPageHowItWorks />
      </div>
      <div id="whyyoullloveit" ref={whyYoullLoveItRef}>
        <TripLandingPageWhyYoullLoveIt />
      </div>
      <div id="whatourtravellerssay" ref={whatOurTravellersSayRef}>
        <TripLandingPageAndTheresMore />
      </div>
      <div id="destinations" ref={destinationsRef}>
        <TripLandingPageTrendingDestinations />
      </div>
      <TripLandingPageFooter />
    </div>
  )
}

export default LoggedOutTripPlannerLandingPage
